'use client'
import { FixedBottomButton } from '@/app/ui/parts/default/sp/item/fixedButton'
import { useNumberSelectorECs } from '@/app/ui/parts/default/sp/item/itemOptionSelectFormV2'
import { ItemNumberSelector } from '@/app/ui/parts/default/sp/item/numberSelector'
import { ItemOptionSelectSlider } from '@/app/ui/parts/default/sp/item/optionslider'
import { BuyButton } from '@/app/ui/parts/default/sp/parts/buttons/buyButton'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { Toaster } from '@/components/ui/sonner'
import { campaignType } from '@/lib/campaign'
import { useAddCartItemOptions, useBuyItemOptions } from '@/lib/cart/logic'
import { getCartItems } from '@/lib/cart/server'
import { itemOptionType, singleItemResponseType } from '@/lib/item/server'
import { serviceType } from '@/lib/service/server'
import { CheckCircle } from 'lucide-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { LikeButton } from '../parts/buttons/likeButton'

type CarouselImageProps = {
  service: serviceType
  item: singleItemResponseType
  liked: boolean
  like_count: number
  one_day_sale?: boolean
  campaigns: campaignType[]
  options: itemOptionType[]
}

export const ItemImageCarouselWithOptions: React.FC<CarouselImageProps> = ({
  service,
  item,
  liked,
  like_count,
  one_day_sale,
  campaigns,
  options,
}) => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [selectedOption, setSelectedOption] = useState<itemOptionType>(
    options[0],
  )
  const [selecting, setSelecting] = useState(false)
  const [quantity, setQuantity] = useState(1)

  const itemMainData = item.item
  const imageData = Array.from(
    new Set(
      options
        .map((opt) => opt.item_image_url)
        .concat(item.item_images.map((img) => img.image_url)),
    ),
  )
  const [loadingBuy, submitBuy] = useBuyItemOptions({
    service,
    item: itemMainData,
  })
  const handleClickBuy = async () => {
    if (selecting) return
    await submitBuy({
      itemOptions: Array(quantity).fill(selectedOption),
    })
  }

  const [loadingAddCart, submitAddCart] = useAddCartItemOptions({
    service,
    item: itemMainData,
  })
  const handleClickAddCart = async () => {
    if (selecting) return
    await submitAddCart({
      itemOptions: Array(quantity).fill(selectedOption),
    })
    const cartItems = await getCartItems(service.service_id)
    toast(``, {
      description: (
        <div className="w-full my-4">
          <p className="font-semibold mb-4">
            <CheckCircle className="inline-block mr-2" />
            カートに追加されました
          </p>
          <div className="flex items-center">
            <img
              src={selectedOption.item_image_url}
              alt={`選択中の商品画像:${item.item.name}`}
              width={50}
              height={50}
              style={{
                objectFit: 'cover',
                verticalAlign: 'center',
                height: '50px',
                width: '50px',
              }}
              className="ml-2"
            />
            <p className="ml-2 text-gray-600 font-semibold">
              {selectedOption.name}{' '}
              {selectedOption.size ? selectedOption.size.name : ''}
              {selectedOption.size && selectedOption.color ? ' / ' : ''}
              {selectedOption.color ? selectedOption.color.name : ''}
              {quantity > 1 ? ` x ${quantity}` : ''}
            </p>
          </div>
          <div className="mt-4 w-full">
            <button
              className="text-sm text-black font-semibold border border-black w-full p-2 mb-2"
              onClick={() => {
                window.location.href = '/transaction/address'
              }}
            >
              購入手続きへ
            </button>
            <button
              className="text-sm text-white font-semibold bg-black w-full p-2"
              onClick={() => {
                window.location.href = '/carts'
              }}
            >
              カートを見る({cartItems ? cartItems.length : 0})
            </button>
          </div>
        </div>
      ),
      position: 'top-center',
      duration: 4000,
      style: {
        display: 'block',
      },
    })
    setQuantity(1)
  }
  const upperText = useMemo(
    () =>
      `選択中の商品: ${selectedOption.name} ${selectedOption.size ? selectedOption.size.name : ''} ${selectedOption.size && selectedOption.color ? ' / ' : ''} ${selectedOption.color ? selectedOption.color.name : ''} ${quantity > 1 ? ` x ${quantity}` : ''}`,
    [selectedOption, quantity],
  )
  const [, setCount] = useState(0)
  useEffect(() => {
    if (!api) {
      return
    }
    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap() + 1)
    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])
  useEffect(() => {
    const optionImageIndex = imageData.findIndex(
      (url) => url === selectedOption.item_image_url,
    )
    if (optionImageIndex !== -1 && api) {
      api.scrollTo(optionImageIndex)
    }
  }, [selectedOption])
  // 今日の日付を取得
  const date = new Date()
  const validCampaigns = campaigns.filter(
    (v) => new Date(v.deadline) > date && v.is_valid,
  )
  return (
    <Fragment>
      <Carousel
        className="w-full"
        setApi={setApi}
        opts={{
          loop: true,
        }}
      >
        <CarouselContent>
          {imageData.map((v, index) => (
            <CarouselItem key={index}>
              <img
                src={v}
                alt={`メイン画像:${index}枚目:${item.item.name}`}
                width={500}
                height={380}
                style={{
                  height: '380px',
                  objectFit: 'contain',
                  verticalAlign: 'center',
                }}
                className="mx-auto"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          style={{
            left: 0,
            background: 'rgba(0,0,0,0.3)',
            border: 'none',
            color: '#fff',
          }}
        />
        <CarouselNext
          style={{
            right: 0,
            background: 'rgba(0,0,0,0.3)',
            border: 'none',
            color: '#fff',
          }}
        />
        <div className="flex overflow-x-auto">
          {imageData.map((v, index) => (
            <img
              key={index}
              src={v}
              alt={`サムネイル:${index}枚目:${item.item.name}`}
              width={70}
              height={70}
              onClick={() => {
                api?.scrollTo(index)
              }}
              style={{
                opacity: current === index + 1 ? 1 : 0.2,
                objectFit: 'cover',
                verticalAlign: 'middle',
                height: '80px',
                width: '80px',
              }}
            />
          ))}
        </div>
        <LikeButton
          service_id={service.service_id}
          item_id={item.item.item_id}
          liked={liked}
          count={like_count}
        />
        {one_day_sale && (
          <div className="one-day-sale-flag main-color absolute bottom-24 left-0 py-2 text-white px-4">
            <span>
              {date.getMonth() + 1}月{date.getDate()}日 23:59まで10%OFF
            </span>
          </div>
        )}
        {validCampaigns.length > 0 && (
          <div className="campaign-flag main-color absolute bottom-24 left-0 py-2 text-white px-4">
            <span>
              {campaigns[0].deadline.slice(0, 10).replaceAll('-', '/')}{' '}
              23:59まで
              {campaigns[0].discount_rate}
              %OFF
            </span>
          </div>
        )}
      </Carousel>
      <div className="item-main-info text-left m-4">
        <h1 className="text-base font-semibold text-[#424242]">
          {item.item.name}
        </h1>
      </div>
      <div className="add-cart-form space-y-4 m-4">
        <div className="w-full mx-auto">
          <ItemOptionSelectSlider
            itemOptions={options}
            selectedOption={selectedOption}
            onChanged={(option) => {
              setSelectedOption(option)
            }}
            onSelecting={(v) => {
              setSelecting(v)
            }}
          />
          {useNumberSelectorECs.includes(service.service_id) && (
            <div className="my-4">
              <ItemNumberSelector
                quantity={quantity}
                setQuantity={setQuantity}
              />
            </div>
          )}
          <div className="my-6">
            <div className="flex w-full mt-2">
              <BuyButton
                loading={loadingBuy}
                disabled={selecting}
                onClick={() => void handleClickBuy()}
                chevron={false}
                promotionModal={false}
              />
            </div>
            <div className="flex w-full mt-2">
              <BuyButton
                loading={loadingAddCart}
                disabled={selecting}
                onClick={() => void handleClickAddCart()}
                text="カートに追加する"
                chevron={false}
                addCart
                promotionModal={false}
              />
            </div>
          </div>
          <Toaster theme="light" />
          <FixedBottomButton
            onClick={() => void handleClickBuy()}
            loading={loadingBuy}
            disabled={selecting}
            text="購入画面に進む"
            upperText={upperText}
          />
        </div>
      </div>
    </Fragment>
  )
}
